import Papa from 'papaparse';
import { CSVData } from '../types';

export interface CsvSyntaxIssue {
  type: 'error' | 'warning';
  code: string;
  message: string;
  row?: number; // 1-indexed row number
  column?: string;
  snippet?: string;
}

export interface DelimiterCandidate {
  char: string;
  name: string;
  count: number;
  avgCols: number;
  score: number;
  consistency: number; // standard deviation (lower = more consistent)
  lineParticipation: number; // percentage of lines containing this delimiter
  isProbable: boolean;
}

export interface ParseOptions {
  delimiter?: string; // e.g. ',', '\t', ';', '|', ':' or 'auto'
  headerRowIndex?: number; // 0-based row index for headers, -1 for no header, or -99 for auto
  hasHeader?: boolean; // explicit boolean toggle for header presence
  encoding?: string;
  trimFields?: boolean;
  quoteChar?: string;
}

export interface ParsedCsvResult {
  csvData: CSVData;
  detectedDelimiter: string;
  detectedDelimiterName: string;
  detectedHeaderRowIndex: number;
  hasHeader: boolean;
  headerDetectionReason: string;
  totalLinesScanned: number;
  rawSampleLines: string[];
  confidence: 'high' | 'medium' | 'low';
  skippedPreambleCount: number;
  syntaxErrors: CsvSyntaxIssue[];
  syntaxWarnings: CsvSyntaxIssue[];
  delimiterCandidates: DelimiterCandidate[];
}

export const COMMON_HEADER_KEYWORDS = new Set([
  'id', 'sku', 'barcode', 'ean', 'upc', 'code', 'name', 'title', 'item', 
  'product', 'price', 'cost', 'qty', 'quantity', 'amount', 'date', 'created', 
  'time', 'exp', 'category', 'type', 'description', 'desc', 'tag', 'url', 
  'link', 'qr', 'email', 'phone', 'address', 'city', 'state', 'zip', 'country',
  'batch', 'lot', 'serial', 'sn', 'tier', 'role', 'company', 'status', 'notes',
  'first_name', 'last_name', 'fullname', 'department', 'designation', 'mrp', 'discount'
]);

/**
 * Detects the most probable delimiter from raw text lines, tracking all candidate scores.
 */
export function detectDelimiter(text: string): { 
  delimiter: string; 
  name: string; 
  confidence: 'high' | 'medium' | 'low';
  candidates: DelimiterCandidate[];
} {
  // Strip BOM if present
  const cleanText = text.replace(/^\uFEFF/, '');
  const lines = cleanText
    .split(/\r\n|\r|\n/)
    .map(l => l.trim())
    .filter(l => l.length > 0 && !l.startsWith('#') && !l.startsWith('//'))
    .slice(0, 30);

  const fallbackCandidates: DelimiterCandidate[] = [
    { char: ',', name: 'Comma (,)', count: 0, avgCols: 1, score: 0, consistency: 0, lineParticipation: 0, isProbable: false },
    { char: '\t', name: 'Tab (\\t)', count: 0, avgCols: 1, score: 0, consistency: 0, lineParticipation: 0, isProbable: false },
    { char: ';', name: 'Semicolon (;)', count: 0, avgCols: 1, score: 0, consistency: 0, lineParticipation: 0, isProbable: false },
    { char: '|', name: 'Pipe (|)', count: 0, avgCols: 1, score: 0, consistency: 0, lineParticipation: 0, isProbable: false }
  ];

  if (lines.length === 0) {
    return { delimiter: ',', name: 'Comma (,)', confidence: 'low', candidates: fallbackCandidates };
  }

  const candidateSpecs = [
    { char: ',', name: 'Comma (,)' },
    { char: '\t', name: 'Tab (\\t)' },
    { char: ';', name: 'Semicolon (;)' },
    { char: '|', name: 'Pipe (|)' },
    { char: ':', name: 'Colon (:)' }
  ];

  const analyzedCandidates: DelimiterCandidate[] = [];

  for (const candidate of candidateSpecs) {
    const counts: number[] = [];
    let totalOccurrences = 0;
    
    for (const line of lines) {
      let count = 0;
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') {
          inQuotes = !inQuotes;
        } else if (c === candidate.char && !inQuotes) {
          count++;
        }
      }
      counts.push(count);
      totalOccurrences += count;
    }

    const nonZeroCounts = counts.filter(c => c > 0);
    const lineParticipation = lines.length > 0 ? (nonZeroCounts.length / lines.length) * 100 : 0;
    
    if (nonZeroCounts.length === 0) {
      analyzedCandidates.push({
        char: candidate.char,
        name: candidate.name,
        count: 0,
        avgCols: 1,
        score: 0,
        consistency: 0,
        lineParticipation: 0,
        isProbable: false
      });
      continue;
    }

    // Calculate mean and standard deviation
    const mean = counts.reduce((a, b) => a + b, 0) / counts.length;
    const variance = counts.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / counts.length;
    const standardDeviation = Math.sqrt(variance);

    // Consistency score: reward higher average column count + lower deviation + high line participation
    const participationFactor = nonZeroCounts.length / lines.length;
    const consistencyBonus = 1 / (standardDeviation + 0.5);
    const score = mean * 3.0 * participationFactor * consistencyBonus;

    analyzedCandidates.push({
      char: candidate.char,
      name: candidate.name,
      count: totalOccurrences,
      avgCols: Math.round(mean) + 1,
      score: parseFloat(score.toFixed(2)),
      consistency: parseFloat(standardDeviation.toFixed(2)),
      lineParticipation: Math.round(lineParticipation),
      isProbable: score >= 1.5 && lineParticipation >= 60
    });
  }

  // Sort candidates by highest score
  analyzedCandidates.sort((a, b) => b.score - a.score);

  const best = analyzedCandidates[0];
  const second = analyzedCandidates[1];

  let bestDelimiter = ',';
  let bestName = 'Comma (,)';
  let confidence: 'high' | 'medium' | 'low' = 'low';

  if (best && best.score > 0 && best.count > 0) {
    bestDelimiter = best.char;
    bestName = best.name;

    if (best.score > 3.5 && best.consistency < 0.5 && best.lineParticipation >= 90) {
      confidence = 'high';
    } else if (best.score > 1.2 && best.lineParticipation >= 50) {
      confidence = 'medium';
    } else {
      confidence = 'low';
    }

    // Check edge case: if second candidate is very close (e.g. semicolon vs comma in decimals)
    if (second && second.score > 0 && (best.score / Math.max(0.1, second.score)) < 1.3) {
      confidence = 'medium';
    }
  }

  return {
    delimiter: bestDelimiter,
    name: bestName,
    confidence,
    candidates: analyzedCandidates
  };
}

/**
 * Inspects a matrix of rows to locate the header row (skipping metadata/preamble rows)
 * and determines if headers exist.
 */
export function detectHeaderRow(rawRows: string[][]): {
  headerRowIndex: number;
  hasHeader: boolean;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
} {
  if (rawRows.length === 0) {
    return {
      headerRowIndex: 0,
      hasHeader: true,
      confidence: 'low',
      reason: 'Empty dataset'
    };
  }

  // Determine standard column count from rows
  const rowLengths = rawRows.slice(0, 15).map(r => r.filter(c => String(c ?? '').trim() !== '').length);
  const maxCols = Math.max(...rowLengths, 1);

  // Scan first 6 lines for true header candidate
  let bestRowIndex = 0;
  let highestScore = -100;
  let isRow0Header = false;
  let headerExplanation = 'Header row detected at Line 1';

  for (let rIdx = 0; rIdx < Math.min(rawRows.length, 6); rIdx++) {
    const row = rawRows[rIdx];
    const populatedCells = row.map(c => String(c ?? '').trim()).filter(c => c !== '');
    
    // If line has only 1 populated cell while data has >= 3 columns, it's likely a title/metadata preamble line
    if (populatedCells.length <= 1 && maxCols >= 3) {
      continue;
    }

    let score = 0;
    let textCells = 0;
    let numberCells = 0;
    let dateCells = 0;
    let keywordHits = 0;
    const foundKeywords: string[] = [];
    const uniqueValues = new Set<string>();

    populatedCells.forEach(cell => {
      uniqueValues.add(cell.toLowerCase());
      const lower = cell.toLowerCase().replace(/[^a-z0-9_]/g, '');

      if (COMMON_HEADER_KEYWORDS.has(lower) || Array.from(COMMON_HEADER_KEYWORDS).some(k => lower.includes(k))) {
        keywordHits++;
        foundKeywords.push(cell);
        score += 3.5;
      }

      if (/^-?\d+(\.\d+)?$/.test(cell)) {
        numberCells++;
      } else if (!isNaN(Date.parse(cell)) && cell.length > 5 && isNaN(Number(cell))) {
        dateCells++;
      } else {
        textCells++;
      }
    });

    // Ratio bonuses
    const total = populatedCells.length;
    if (total > 0) {
      const textRatio = textCells / total;
      const numberRatio = numberCells / total;
      const uniquenessRatio = uniqueValues.size / total;

      score += textRatio * 5;
      score -= numberRatio * 7; // Headers rarely contain pure numbers
      score += uniquenessRatio * 3.5; // Headers should be unique

      // Compare with the next row (data row) if available
      const nextRow = rawRows[rIdx + 1];
      if (nextRow) {
        let nextRowNumberCount = 0;
        nextRow.forEach(c => {
          if (/^-?\d+(\.\d+)?$/.test(String(c ?? '').trim())) nextRowNumberCount++;
        });
        if (nextRowNumberCount > numberCells) {
          score += 4.5; // High contrast between header (text) and data (numbers)
        }
      }
    }

    if (rIdx === 0 && score > 2) {
      isRow0Header = true;
    }

    if (score > highestScore) {
      highestScore = score;
      bestRowIndex = rIdx;
      if (foundKeywords.length > 0) {
        headerExplanation = `Line ${rIdx + 1} contains recognized header keywords (${foundKeywords.slice(0, 3).join(', ')})`;
      } else if (rIdx > 0) {
        headerExplanation = `Line ${rIdx + 1} identified as column header (skipped ${rIdx} preamble title line${rIdx > 1 ? 's' : ''})`;
      } else {
        headerExplanation = `Line 1 identified as column names (${textCells} text column${textCells !== 1 ? 's' : ''})`;
      }
    }
  }

  // If score is negative (e.g. all rows are pure numbers), dataset is headerless
  if (highestScore < 0 && !isRow0Header) {
    return {
      headerRowIndex: -1,
      hasHeader: false,
      confidence: 'medium',
      reason: 'No textual header detected; data rows appear numeric/raw'
    };
  }

  return {
    headerRowIndex: bestRowIndex,
    hasHeader: true,
    confidence: highestScore > 5 ? 'high' : highestScore > 2 ? 'medium' : 'low',
    reason: headerExplanation
  };
}

/**
 * Robust CSV/TSV parser with auto-delimiter and auto-header detection,
 * supporting manual overrides for delimiter, header rows, and quotes.
 */
export function parseDelimitedText(
  rawText: string,
  options?: ParseOptions
): ParsedCsvResult {
  const cleanText = rawText.replace(/^\uFEFF/, '').trim();
  const rawLines = cleanText.split(/\r\n|\r|\n/).filter(l => l.trim() !== '');

  // 1. Detect or use specified delimiter
  const detected = detectDelimiter(cleanText);
  const delimiter = options?.delimiter && options.delimiter !== 'auto' 
    ? options.delimiter 
    : detected.delimiter;

  // 2. Initial matrix parse with PapaParse (header: false)
  const initialParse = Papa.parse<string[]>(cleanText, {
    delimiter,
    header: false,
    skipEmptyLines: 'greedy'
  });

  // Extract syntax diagnostics from PapaParse errors
  const syntaxErrors: CsvSyntaxIssue[] = [];
  const syntaxWarnings: CsvSyntaxIssue[] = [];

  (initialParse.errors || []).forEach(err => {
    const rowNum = err.row !== undefined ? err.row + 1 : undefined;
    const issue: CsvSyntaxIssue = {
      type: err.type === 'Quotes' || err.code === 'UndetectableDelimiter' ? 'error' : 'warning',
      code: err.code,
      message: err.message,
      row: rowNum,
      snippet: rowNum && rawLines[rowNum - 1] ? rawLines[rowNum - 1].substring(0, 80) : undefined
    };

    if (issue.type === 'error') {
      syntaxErrors.push(issue);
    } else {
      syntaxWarnings.push(issue);
    }
  });

  // Check for global unclosed quotation marks if PapaParse had errors
  if (syntaxErrors.length === 0 && (initialParse.errors && initialParse.errors.length > 0)) {
    const totalQuotes = (cleanText.match(/"/g) || []).length;
    if (totalQuotes % 2 !== 0) {
      syntaxErrors.push({
        type: 'error',
        code: 'UnclosedQuote',
        message: 'Unmatched quotation mark detected in dataset.',
      });
    }
  }

  const matrix = (initialParse.data || []).filter(row => Array.isArray(row) && row.some(c => String(c ?? '').trim() !== ''));

  if (matrix.length === 0) {
    return {
      csvData: { headers: [], rows: [] },
      detectedDelimiter: delimiter,
      detectedDelimiterName: getDelimiterName(delimiter),
      detectedHeaderRowIndex: 0,
      hasHeader: false,
      headerDetectionReason: 'No data records found in file',
      totalLinesScanned: 0,
      rawSampleLines: rawLines.slice(0, 5),
      confidence: 'low',
      skippedPreambleCount: 0,
      syntaxErrors,
      syntaxWarnings,
      delimiterCandidates: detected.candidates
    };
  }

  // 3. Detect or use specified header row
  let headerIndex = 0;
  let hasHeader = true;
  let detectionReason = '';

  if (options?.hasHeader === false || options?.headerRowIndex === -1) {
    hasHeader = false;
    headerIndex = -1;
    detectionReason = 'Manual override: No header row (columns auto-numbered)';
  } else if (options?.headerRowIndex !== undefined && options.headerRowIndex !== -99) {
    headerIndex = Math.max(0, Math.min(matrix.length - 1, options.headerRowIndex));
    hasHeader = true;
    detectionReason = `Manual override: Using Line ${headerIndex + 1} as header row`;
  } else {
    const headerDetection = detectHeaderRow(matrix);
    headerIndex = headerDetection.headerRowIndex;
    hasHeader = headerDetection.hasHeader;
    detectionReason = headerDetection.reason;
  }

  // 4. Construct Headers
  let headers: string[] = [];
  let dataRowsMatrix: string[][] = [];
  let skippedPreambleCount = 0;

  if (hasHeader && headerIndex >= 0 && headerIndex < matrix.length) {
    const rawHeaderRow = matrix[headerIndex];
    skippedPreambleCount = headerIndex;
    dataRowsMatrix = matrix.slice(headerIndex + 1);

    const seenHeaders: Record<string, number> = {};
    headers = rawHeaderRow.map((col, idx) => {
      let name = String(col ?? '').trim();
      if (!name) {
        name = `Column_${idx + 1}`;
      }
      if (seenHeaders[name] !== undefined) {
        seenHeaders[name]++;
        return `${name}_${seenHeaders[name]}`;
      }
      seenHeaders[name] = 1;
      return name;
    });
  } else {
    // Headerless dataset
    dataRowsMatrix = matrix;
    const maxCols = Math.max(...matrix.map(r => r.length), 1);
    headers = Array.from({ length: maxCols }, (_, i) => `Column_${i + 1}`);
  }

  // 5. Build Row Objects
  const rows: Record<string, any>[] = dataRowsMatrix.map(rowArray => {
    const rowObj: Record<string, any> = {};
    headers.forEach((headerName, idx) => {
      const val = rowArray[idx];
      rowObj[headerName] = val !== undefined && val !== null ? String(val).trim() : '';
    });
    return rowObj;
  }).filter(row => Object.values(row).some(v => v !== ''));

  return {
    csvData: {
      headers,
      rows
    },
    detectedDelimiter: delimiter,
    detectedDelimiterName: getDelimiterName(delimiter),
    detectedHeaderRowIndex: headerIndex,
    hasHeader,
    headerDetectionReason: detectionReason,
    totalLinesScanned: matrix.length,
    rawSampleLines: rawLines.slice(0, 6),
    confidence: detected.confidence,
    skippedPreambleCount,
    syntaxErrors,
    syntaxWarnings,
    delimiterCandidates: detected.candidates
  };
}

export function getDelimiterName(char: string): string {
  switch (char) {
    case ',': return 'Comma (,)';
    case '\t': return 'Tab (\\t)';
    case ';': return 'Semicolon (;)';
    case '|': return 'Pipe (|)';
    case ':': return 'Colon (:)';
    default: return `Custom ('${char}')`;
  }
}
